// services/assessmentTemplateService.ts
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  writeBatch,
  arrayUnion,
  arrayRemove
} from "firebase/firestore";
import { db } from "../firebase/config";

export interface AssessmentTemplate {
  id: string;
  name: string;
  description: string;
  targetAudience: "student" | "employee" | "leader" | "all";
  sections: {
    sectionId: string;
    sectionName: string;
    traits: string[];
    questionSelectionRule: "random" | "fixed";
    numberOfQuestions: number;
    order: number;
  }[];
  totalQuestions: number;
  totalDuration: number;
  instructions: string;
  timeLimit: number;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface AssessmentTemplateForm {
  name: string;
  description: string;
  targetAudience: "student" | "employee" | "leader" | "all";
  sections: {
    sectionId: string;
    sectionName: string;
    traits: string[];
    questionSelectionRule: "random" | "fixed";
    numberOfQuestions: number;
    order: number;
  }[];
  instructions: string;
  timeLimit: number;
  isActive: boolean;
}

const TEMPLATES_COLLECTION = "assessmentTemplates";

// Get all assessment templates
export const getAssessmentTemplates = async (): Promise<AssessmentTemplate[]> => {
  try {
    const q = query(collection(db, TEMPLATES_COLLECTION), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);

    const templates = [];

    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();

      let displayName = "System";

      if (data.organizationName) {
        displayName = data.organizationName;
      } else if (data.createdBy) {
        const userQuery = query(
          collection(db, "users"),
          where("uid", "==", data.createdBy)
        );

        const userSnap = await getDocs(userQuery);

        if (!userSnap.empty) {
          const userData = userSnap.docs[0].data();

          if (userData.role === "super_admin") {
            displayName = "Super Admin";
          } else if (userData.organizationId) {
            const orgDoc = await getDoc(doc(db, "organizations", userData.organizationId));
            if (orgDoc.exists()) {
              displayName = orgDoc.data().name;
            } else {
              displayName = userData.name || "User";
            }
          } else {
            displayName = userData.name || "User";
          }
        }
      }

      templates.push({
        id: docSnap.id,
        name: data.name || "",
        description: data.description || "",
        targetAudience: data.targetAudience || "all",
        sections: data.sections || [],
        totalQuestions: data.totalQuestions || 0,
        totalDuration: data.totalDuration || 0,
        instructions: data.instructions || "",
        timeLimit: data.timeLimit || 0,
        isActive: data.isActive || false,
        createdBy: data.createdBy || "",
        createdByOrgName: displayName || "System",
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      });
    }

    return templates;

  } catch (error) {
    console.error("Error fetching assessment templates:", error);
    throw error;
  }
};

// Get assessment template by ID
export const getAssessmentTemplateById = async (id: string): Promise<AssessmentTemplate | null> => {
  try {
    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name || "",
        description: data.description || "",
        targetAudience: data.targetAudience || "all",
        sections: data.sections || [],
        totalQuestions: data.totalQuestions || 0,
        totalDuration: data.totalDuration || 0,
        instructions: data.instructions || "",
        timeLimit: data.timeLimit || 0,
        isActive: data.isActive || false,
        createdBy: data.createdBy || "",
        createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: data.updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as AssessmentTemplate;
    }
    return null;
  } catch (error) {
    console.error("Error fetching assessment template:", error);
    throw error;
  }
};

// Create assessment template
export const createAssessmentTemplate = async (
  templateData: AssessmentTemplateForm,
  userId: string,
  organizationId: string,
  organizationName: string,
  role: string
): Promise<string> => {
  try {
    const now = Timestamp.now();

    const totalQuestions = templateData.sections.reduce((sum, s) => sum + s.numberOfQuestions, 0);
    const totalDuration = templateData.sections.reduce((sum, s) => {
      return sum + (s.numberOfQuestions * 1.5);
    }, 0);

    const docRef = await addDoc(collection(db, TEMPLATES_COLLECTION), {
      ...templateData,
      totalQuestions,
      totalDuration: templateData.timeLimit || totalDuration,
      createdBy: userId,
      organizationId: organizationId,
      organizationName: role === "super_admin" ? "Super Admin" : organizationName,
      createdAt: now,
      updatedAt: now,
    });

    return docRef.id;
  } catch (error) {
    console.error("Error creating assessment template:", error);
    throw error;
  }
};

// Update assessment template
export const updateAssessmentTemplate = async (
  id: string, 
  templateData: Partial<AssessmentTemplateForm>
): Promise<void> => {
  try {
    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    const updateData: any = {
      ...templateData,
      updatedAt: Timestamp.now(),
    };
    
    // Recalculate totals if sections changed
    if (templateData.sections) {
      updateData.totalQuestions = templateData.sections.reduce((sum, s) => sum + s.numberOfQuestions, 0);
      updateData.totalDuration = templateData.timeLimit || 
        templateData.sections.reduce((sum, s) => sum + (s.numberOfQuestions * 1.5), 0);
    }
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error updating assessment template:", error);
    throw error;
  }
};

// Delete assessment template
export const deleteAssessmentTemplate = async (id: string): Promise<void> => {
  try {
    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting assessment template:", error);
    throw error;
  }
};

// Toggle template active status
export const toggleTemplateStatus = async (id: string, isActive: boolean): Promise<void> => {
  try {
    const docRef = doc(db, TEMPLATES_COLLECTION, id);
    await updateDoc(docRef, {
      isActive,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error toggling template status:", error);
    throw error;
  }
};

// Duplicate template
export const duplicateTemplate = async (id: string, userId: string): Promise<string> => {
  try {
    const original = await getAssessmentTemplateById(id);
    if (!original) throw new Error("Template not found");

    // Get logged in user from localStorage
    const storedUser = JSON.parse(localStorage.getItem("currentUser") || "{}");

    const newTemplate: AssessmentTemplateForm = {
      name: `${original.name} (Copy)`,
      description: original.description,
      targetAudience: original.targetAudience,
      sections: original.sections,
      instructions: original.instructions,
      timeLimit: original.timeLimit,
      isActive: false,
    };

    return await createAssessmentTemplate(
      newTemplate,
      storedUser.id || storedUser.uid || userId,
      storedUser.organizationId || "",
      storedUser.organizationName || "",
      storedUser.role || ""
    );
  } catch (error) {
    console.error("Error duplicating template:", error);
    throw error;
  }
};